import jsrsasign from 'jsrsasign';
import { CryptoProps } from '../types';
import { logGenerateCSR, logGenerateKeys, logSign } from '@byted/tiktok-zti-monitor';
import { BaseSecurityCrypto } from './base';

class SecurityCrypto extends BaseSecurityCrypto {
  constructor(props: CryptoProps) {
    super(props);
    const { privateKey, publicKey } = props;
    if (!(privateKey && publicKey)) {
      const start = Date.now();
      const { prvKeyObj, pubKeyObj } = jsrsasign.KEYUTIL.generateKeypair('EC', 'secp256r1');
      this._public_key = jsrsasign.KEYUTIL.getPEM(pubKeyObj);
      this._private_key = jsrsasign.KEYUTIL.getPEM(prvKeyObj, 'PKCS8PRV');
      logGenerateKeys({
        duration: Date.now() - start,
        type: 'jsrsasign'
      });
    } else {
      this._public_key = publicKey;
      this._private_key = privateKey;
    }
  }

  /**
   * 获取当前keys
   * @returns  privateKey & publickKey
   */
  getKeys = () => ({ publicKey: this._public_key, privateKey: this._private_key });

  /**
   * sign 加签数据
   * @param sign_str string  需要加签的数据
   * @returns string 加签之后的数据
   */
  // eslint-disable-next-line require-await
  sign = async (sign_str: string): Promise<string> => {
    const start = Date.now();
    const sign_key = jsrsasign.KEYUTIL.getKey(this._private_key || '');
    const sig = new jsrsasign.KJUR.crypto.Signature({
      alg: 'SHA256withECDSA'
    });
    sig.init(sign_key);
    const sig_data = sig.signString(sign_str);
    logSign({
      duration: Date.now() - start,
      type: 'jsrsasign'
    });
    return sig_data;
  };

  // /**
  //  * 验证当前证书
  //  * @param sign_str 当前加签数据
  //  * @param cert 当前证书
  //  * @returns boolean
  //  */
  // verify = (sign_str: string, origin_str: string, cert: string): boolean => {
  //   let sig2 = new jsrsasign.KJUR.crypto.Signature({ alg: 'SHA256withECDSA' });
  //   sig2.init(cert);
  //   sig2.updateString(origin_str);
  //   let isValid = sig2.verify(sign_str);
  //   return isValid;
  // };

  /**
   * 验证当前证书
   * @param sign_str 当前加签数据
   * @param cert 当前证书
   * @returns boolean
   */
  verify = (sign_str: string, origin_str: string, cert?: string): boolean => {
    const sig2 = new jsrsasign.KJUR.crypto.Signature({ alg: 'SHA256withECDSA' });
    if (cert) {
      sig2.init(cert);
    } else {
      const sign_key = jsrsasign.KEYUTIL.getKey(this._public_key || '');
      sig2.init(sign_key);
    }
    sig2.updateString(origin_str);
    const isValid = sig2.verify(sign_str);
    return isValid;
  };

  /**
   * 获取CSR
   * @returns csr pem
   */
  getCSR = () => {
    const start = Date.now();
    const csr = new jsrsasign.KJUR.asn1.csr.CertificationRequest({
      subject: { str: '/C=CN/CN=tt_ticket_guard' },
      sbjpubkey: this._public_key || '',
      sigalg: 'SHA256withECDSA',
      extreq: [{ extname: 'subjectAltName', array: [{ dns: window?.location?.hostname || '' }] }],
      sbjprvkey: this._private_key || ''
    });
    const pem = csr.getPEM();
    logGenerateCSR({
      duration: Date.now() - start,
      type: 'jsrsasign'
    });
    return pem;
  };

  /**
   * 比较公私钥是否匹配
   * 方式还有点问题，目前暂时可用
   * @param priKey PKCS格式
   * @param pubKey PKCS格式
   */
  comparePubKey = (priKey: string, pubKey: string) => {
    const rsa = jsrsasign.KEYUTIL.getKey(priKey);

    const pubKeyHex = jsrsasign.pemtohex(pubKey);
    const pubKeyHexFromPri = (rsa as any).generatePublicKeyHex();

    // wip:公私钥判断方式，有点问题，不知道为什么
    // pem格式解出来的公钥始终比从私钥中解出来的长，原因需要进一步明晰
    const res = pubKeyHex.includes(pubKeyHexFromPri);
    return res;
  };
}

export default SecurityCrypto;
